/*
	This hook returns a function that dispatches the shiftSelect action. The shiftSelect action is used to
	select multiple grid items by clicking on a row and then clicking on another row while holding shift.

	We pass the function to a mouseDown event as this runs before onChange, which is used for selecting single grid items
	via checkboxes, and because we can prevent the browser from selecting all the text between the first and last item clicked
	with evt.preventDefault().

	Arguments:
		The hook has 3 arguments, id, type, and config.

			id - required. This should be the id of the row that was clicked.
			type - required. This should correspond to the type of item that was clicked, e.g. creative, version, asset etc.
			config - optional.

	Config:
		Config allows the user to pass 3 optional functions to the shiftSelect action:

			selectItems:
				This takes 2 arguments, state and level, and is designed to give the user a more flexible option
				for getting all of the items of a certain `type` from the redux store, or passing a custom array
				of items if they don't exist in the store.

			buildItemId:
				This takes a single argument, item. It's used to build an id from the item for instances where the
				id in the selected store and the id of the item in state don't match.
				e.g. assets are passed to the selected store as `assetType--123` but are stored in state as { type: assetType, id: 123 }
				so we use buildItemId to build an id which can be compared to the id in the selected store.

			buildSelectedList:
				This takes 5 arguments, first, last, items, selectedItems, and buildItemId.

					first - the index of the item last added to the selected store.
					last -  the index of the item the user shift-clicked on.
					items - the items in the redux store which correspond to the type of item the user clicked, or the custom items
							passed in with selectItems.
					selectedItems - the items currently in the selected store.
					buildItemId - the same function passed to the config.

		Example usage:

			export function SelectItem({ dependentValues }){
				let dispatch = useDispatch();
				const {id, type, shiftSelectConfig = {} } = dependentValues;
				let shiftClick = useShiftClick(id, type, shiftSelectConfig);


				return (
					<label
						className="select-holder"
						onMouseDown={shiftClick}
					>
						<input
							type="checkbox"
							onChange={(evt) => {
								//	We need this to prevent the onChange behaviour from firing if the user is holding down shift.
								//	onChange fires after mouseDown, so without it, items are selected by shiftSelect, then deselected
								//	by toggleSelectedItem().
								if(evt.nativeEvent.shiftKey){ return; }

								dispatch(toggleSelectedItem(id, type, evt.target.checked));
							}}
						/>
						<span className="pretty-checkbox" />
					</label>
				);
}
*/
import { useDispatch } from "react-redux";
import { shiftSelect } from "reducers/Selected";

export function useShiftClick(id, type, config){
	const dispatch = useDispatch();

	return function shiftClick(evt) {
		if(!evt.nativeEvent.shiftKey){ return; }
		//We use this to prevent the browser from selecting (and highlighting) all the text between the first and last item clicked.
		evt.preventDefault();
		dispatch(shiftSelect(id, type, config));
	};
}
